// frontend/src/components/Product/ProductComments.jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import {
  getComments,
  addComment,
  markCommentHelpful,
  deleteComment,
} from "../../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCircle,
  faThumbsUp,
  faTrash,
  faReply,
  faSpinner,
  faTractor,
  faStore,
  faCheckCircle,
  faClock,
  faStar,
  faStar as faStarSolid,
} from "@fortawesome/free-solid-svg-icons";
import Loader from "../UI/Loader";

// ============================================
// BRAND CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

// ============================================
// MAIN COMPONENT
// ============================================
const ProductComments = ({ productId }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [replyText, setReplyText] = useState({});
  const [replyingTo, setReplyingTo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // ============================================
  // FETCH COMMENTS
  // ============================================
  useEffect(() => {
    fetchComments();
  }, [productId]);

  const fetchComments = async () => {
    try {
      const { data } = await getComments(productId);
      setComments(data.comments);
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // HANDLERS
  // ============================================
  const handleSubmitComment = async (e, parentId = null) => {
    e.preventDefault();
    const text = parentId ? replyText[parentId] : commentText;

    if (!text?.trim()) return;
    if (!user) {
      alert("Please login to comment");
      return;
    }

    setSubmitting(true);
    try {
      await addComment(productId, text, parentId);
      setCommentText("");
      if (parentId) {
        setReplyText({ ...replyText, [parentId]: "" });
        setReplyingTo(null);
      }
      fetchComments();
    } catch (error) {
      console.error("Error adding comment:", error);
      alert(error.response?.data?.message || "Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleHelpful = async (commentId) => {
    if (!user) {
      alert("Please login to mark comments as helpful");
      return;
    }
    try {
      await markCommentHelpful(commentId);
      fetchComments();
    } catch (error) {
      console.error("Error marking helpful:", error);
    }
  };

  const handleDelete = async (commentId) => {
    if (window.confirm("Are you sure you want to delete this comment?")) {
      try {
        await deleteComment(commentId);
        fetchComments();
      } catch (error) {
        console.error("Error deleting comment:", error);
        alert(error.response?.data?.message || "Failed to delete comment");
      }
    }
  };

  // ============================================
  // HELPERS
  // ============================================
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getUserIcon = (userType) => {
    return userType === "farmer" ? faTractor : faStore;
  };

  const getUserTypeLabel = (userType) => {
    return userType === "farmer" ? "🌾 Farmer" : "🛒 Customer";
  };

  const displayedComments = showAll ? comments : comments.slice(0, 3);

  // ============================================
  // RENDER
  // ============================================
  if (loading) return <Loader size="small" />;

  return (
    <motion.div 
      className="product-comments"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="comments-header">
        <h3>
          <FontAwesomeIcon icon={faStarSolid} />
          Customer Reviews & Comments
          <span className="comment-count">({comments.length})</span>
        </h3>
      </div>

      {/* ========== COMMENT FORM ========== */}
      {user && (
        <motion.form
          onSubmit={handleSubmitComment}
          className="comment-form"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="comment-input-wrapper">
            <div className="comment-avatar">
              {user.avatar ? (
                <img src={user.avatar} alt={user.name} />
              ) : (
                <FontAwesomeIcon icon={faUserCircle} />
              )}
            </div>
            <textarea
              placeholder="Share your experience with this product..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              rows="3"
              className="comment-textarea"
            />
          </div>
          <div className="comment-form-actions">
            <span className="comment-user-type">{getUserTypeLabel(user.userType)}</span>
            <button
              type="submit"
              className="submit-btn"
              disabled={submitting || !commentText.trim()}
            >
              {submitting ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                "Post Comment"
              )}
            </button>
          </div>
        </motion.form>
      )}

      {/* ========== COMMENTS LIST ========== */}
      <div className="comments-list">
        {comments.length === 0 ? (
          <div className="no-comments">
            <div className="no-comments-icon">💬</div>
            <p>Be the first to comment on this product!</p>
          </div>
        ) : (
          displayedComments.map((comment, index) => (
            <motion.div
              key={comment._id}
              className="comment-item"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="comment-header">
                <div className="comment-user">
                  <FontAwesomeIcon
                    icon={getUserIcon(comment.userType)}
                    className="comment-user-icon"
                  />
                  <div className="comment-user-info">
                    <span className="comment-user-name">{comment.userName}</span>
                    <span className="comment-user-type">
                      {getUserTypeLabel(comment.userType)}
                    </span>
                    <span className="comment-date">
                      <FontAwesomeIcon icon={faClock} />
                      {formatDate(comment.createdAt)}
                    </span>
                  </div>
                </div>
                {(user?._id === comment.user || user?.role === "admin") && (
                  <button
                    className="delete-comment"
                    onClick={() => handleDelete(comment._id)}
                    aria-label="Delete comment"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>

              <div className="comment-content">{comment.comment}</div>

              <div className="comment-actions">
                <button
                  className="helpful-btn"
                  onClick={() => handleHelpful(comment._id)}
                >
                  <FontAwesomeIcon icon={faThumbsUp} />
                  Helpful ({comment.helpfulCount})
                </button>
                <button
                  className="reply-btn"
                  onClick={() =>
                    setReplyingTo(replyingTo === comment._id ? null : comment._id)
                  }
                >
                  <FontAwesomeIcon icon={faReply} />
                  Reply
                </button>
              </div>

              {/* Reply Form */}
              <AnimatePresence>
                {replyingTo === comment._id && (
                  <motion.form
                    onSubmit={(e) => handleSubmitComment(e, comment._id)}
                    className="reply-form"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <textarea
                      placeholder="Write a reply..."
                      value={replyText[comment._id] || ""}
                      onChange={(e) =>
                        setReplyText({
                          ...replyText,
                          [comment._id]: e.target.value,
                        })
                      }
                      rows="2"
                    />
                    <div className="reply-actions">
                      <button
                        type="submit"
                        className="reply-submit"
                        disabled={submitting || !replyText[comment._id]?.trim()}
                      >
                        {submitting ? (
                          <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                          "Post Reply"
                        )}
                      </button>
                      <button
                        type="button"
                        className="reply-cancel"
                        onClick={() => setReplyingTo(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>

              {/* Replies */}
              {comment.replies?.length > 0 && (
                <div className="replies-list">
                  {comment.replies.map((reply) => (
                    <div key={reply._id} className="reply-item">
                      <div className="reply-header">
                        <div className="reply-user">
                          <FontAwesomeIcon
                            icon={getUserIcon(reply.userType)}
                            className="reply-user-icon"
                          />
                          <div className="reply-user-info">
                            <span className="reply-user-name">
                              {reply.userName}
                            </span>
                            <span className="reply-user-type">
                              {getUserTypeLabel(reply.userType)}
                            </span>
                            <span className="reply-date">
                              <FontAwesomeIcon icon={faClock} />
                              {formatDate(reply.createdAt)}
                            </span>
                          </div>
                        </div>
                        {(user?._id === reply.user || user?.role === "admin") && (
                          <button
                            className="delete-comment"
                            onClick={() => handleDelete(reply._id)}
                            aria-label="Delete reply"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        )}
                      </div>
                      <div className="reply-content">{reply.comment}</div>
                      <div className="reply-actions">
                        <button
                          className="helpful-btn"
                          onClick={() => handleHelpful(reply._id)}
                        >
                          <FontAwesomeIcon icon={faThumbsUp} />
                          Helpful ({reply.helpfulCount})
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          ))
        )}
      </div>

      {/* Show More / Show Less */}
      {comments.length > 3 && (
        <div className="show-more">
          <button onClick={() => setShowAll(!showAll)}>
            {showAll ? "Show Less" : `Show All ${comments.length} Comments`}
          </button>
        </div>
      )}

      {/* ============================================
          STYLES
          ============================================ */}
      <style>{`
        .product-comments {
          margin-top: 2.5rem;
          padding-top: 2rem;
          border-top: 2px solid #f0f0f0;
        }

        .comments-header {
          margin-bottom: 1.5rem;
        }

        .comments-header h3 {
          font-size: clamp(1rem, 1.5vw, 1.25rem);
          color: #1F1F1F;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .comments-header h3 svg {
          color: #D9A321;
        }

        .comment-count {
          font-size: 0.8rem;
          font-weight: 400;
          color: #5A5A5A;
          background: #F8F8F8;
          padding: 0.1rem 0.5rem;
          border-radius: 12px;
          margin-left: 0.25rem;
        }

        .comment-form {
          background: #F8F8F8;
          border-radius: 12px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          border: 1px solid #e5e5e5;
        }

        .comment-input-wrapper {
          display: flex;
          gap: 0.75rem;
          align-items: flex-start;
        }

        .comment-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #006B2E, #014421);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          font-size: 1.25rem;
          overflow: hidden;
          flex-shrink: 0;
        }

        .comment-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .comment-textarea {
          flex: 1;
          padding: 0.5rem 0.75rem;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          font-size: 0.875rem;
          font-family: "Inter", sans-serif;
          resize: vertical;
          min-height: 60px;
          transition: border-color 0.3s ease;
          background: #ffffff;
          color: #1F1F1F;
        }

        .comment-textarea:focus {
          outline: none;
          border-color: #D9A321;
        }

        .comment-form-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 0.5rem;
          padding-left: 3.25rem;
        }

        .comment-user-type {
          font-size: 0.7rem;
          color: #5A5A5A;
        }

        .submit-btn {
          padding: 0.4rem 1.25rem;
          background: #D9A321;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.813rem;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: "Inter", sans-serif;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .submit-btn:hover:not(:disabled) {
          background: #B8860B;
          transform: translateY(-2px);
        }

        .submit-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .no-comments {
          text-align: center;
          padding: 2rem 1rem;
          color: #5A5A5A;
        }

        .no-comments-icon {
          font-size: 2.5rem;
          margin-bottom: 0.5rem;
        }

        .no-comments p {
          margin: 0;
        }

        .comments-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .comment-item {
          background: #ffffff;
          border-radius: 12px;
          padding: 1rem;
          border: 1px solid #f0f0f0;
          transition: all 0.3s ease;
        }

        .comment-item:hover {
          border-color: #D9A321;
        }

        .comment-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.5rem;
        }

        .comment-user {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .comment-user-icon {
          font-size: 1.25rem;
          color: #006B2E;
          background: #e8f5e9;
          padding: 0.3rem;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .comment-user-info {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.25rem 0.5rem;
        }

        .comment-user-name {
          font-weight: 600;
          color: #1F1F1F;
          font-size: 0.875rem;
        }

        .comment-user-type {
          font-size: 0.65rem;
          color: #006B2E;
          background: #e8f5e9;
          padding: 0.05rem 0.4rem;
          border-radius: 8px;
        }

        .comment-date {
          font-size: 0.65rem;
          color: #5A5A5A;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .comment-date svg {
          font-size: 0.5rem;
        }

        .delete-comment {
          background: none;
          border: none;
          color: #dc2626;
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 4px;
          transition: all 0.3s ease;
        }

        .delete-comment:hover {
          background: #fee2e2;
        }

        .comment-content {
          color: #1F1F1F;
          font-size: 0.875rem;
          line-height: 1.6;
          margin-bottom: 0.5rem;
          padding-left: 3.25rem;
        }

        .comment-actions {
          display: flex;
          gap: 1rem;
          padding-left: 3.25rem;
        }

        .helpful-btn,
        .reply-btn {
          background: none;
          border: none;
          color: #5A5A5A;
          font-size: 0.75rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.3rem;
          transition: all 0.3s ease;
          font-family: "Inter", sans-serif;
          padding: 0.15rem 0.5rem;
          border-radius: 4px;
        }

        .helpful-btn:hover,
        .reply-btn:hover {
          color: #D9A321;
          background: #fef3c7;
        }

        .reply-form {
          margin-top: 0.5rem;
          padding-left: 3.25rem;
        }

        .reply-form textarea {
          width: 100%;
          padding: 0.5rem 0.75rem;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          font-size: 0.813rem;
          font-family: "Inter", sans-serif;
          resize: vertical;
          min-height: 50px;
          transition: border-color 0.3s ease;
          background: #F8F8F8;
          color: #1F1F1F;
        }

        .reply-form textarea:focus {
          outline: none;
          border-color: #D9A321;
        }

        .reply-actions {
          display: flex;
          gap: 0.5rem;
          margin-top: 0.25rem;
        }

        .reply-submit {
          padding: 0.3rem 1rem;
          background: #D9A321;
          color: #ffffff;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          font-size: 0.75rem;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: "Inter", sans-serif;
          display: flex;
          align-items: center;
          gap: 0.3rem;
        }

        .reply-submit:hover:not(:disabled) {
          background: #B8860B;
        }

        .reply-submit:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .reply-cancel {
          padding: 0.3rem 1rem;
          background: transparent;
          color: #5A5A5A;
          border: 1px solid #e5e5e5;
          border-radius: 6px;
          font-size: 0.75rem;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: "Inter", sans-serif;
        }

        .reply-cancel:hover {
          background: #f0f0f0;
        }

        .replies-list {
          margin-top: 0.5rem;
          padding-left: 3.25rem;
          border-left: 3px solid #D9A321;
          padding-left: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .reply-item {
          background: #F8F8F8;
          border-radius: 8px;
          padding: 0.75rem;
        }

        .reply-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.25rem;
        }

        .reply-user {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .reply-user-icon {
          font-size: 0.875rem;
          color: #006B2E;
          background: #e8f5e9;
          padding: 0.2rem;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .reply-user-info {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.2rem 0.4rem;
        }

        .reply-user-name {
          font-weight: 600;
          color: #1F1F1F;
          font-size: 0.813rem;
        }

        .reply-user-type {
          font-size: 0.6rem;
          color: #006B2E;
          background: #e8f5e9;
          padding: 0.05rem 0.3rem;
          border-radius: 6px;
        }

        .reply-date {
          font-size: 0.6rem;
          color: #5A5A5A;
          display: flex;
          align-items: center;
          gap: 0.2rem;
        }

        .reply-date svg {
          font-size: 0.45rem;
        }

        .reply-content {
          color: #1F1F1F;
          font-size: 0.813rem;
          line-height: 1.5;
          padding-left: 2.25rem;
        }

        .reply-actions {
          padding-left: 2.25rem;
          margin-top: 0.25rem;
        }

        .show-more {
          text-align: center;
          margin-top: 1rem;
        }

        .show-more button {
          background: none;
          border: none;
          color: #D9A321;
          font-weight: 600;
          font-size: 0.875rem;
          cursor: pointer;
          transition: color 0.3s ease;
          font-family: "Inter", sans-serif;
        }

        .show-more button:hover {
          color: #B8860B;
          text-decoration: underline;
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 768px) {
          .comment-input-wrapper {
            flex-direction: column;
            align-items: stretch;
          }

          .comment-form-actions {
            padding-left: 0;
          }

          .comment-content {
            padding-left: 0;
          }

          .comment-actions {
            padding-left: 0;
          }

          .reply-form {
            padding-left: 0;
          }

          .replies-list {
            padding-left: 0.75rem;
          }

          .reply-content {
            padding-left: 0;
          }

          .reply-actions {
            padding-left: 0;
          }
        }

        @media (max-width: 480px) {
          .comment-header {
            flex-wrap: wrap;
          }

          .comment-user {
            flex-wrap: wrap;
          }

          .comment-user-info {
            flex-direction: column;
            align-items: flex-start;
          }

          .comment-actions {
            flex-wrap: wrap;
          }

          .reply-actions {
            flex-wrap: wrap;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default ProductComments;